import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { DatePicker, Modal, Table, TableProps } from "antd";
import dayjs from "dayjs";
import { FaBoxOpen, FaEye, FaSearch, FaUndo } from "react-icons/fa";
import Spinner from "../../components/shared/Spinner/Spinner";
import MainButton from "../../components/shared/MainButton/MainButton";
import {
  ProductOrderItemType,
  ProductOrdersFilterParams,
} from "../../interfaces/admin.interface";
import { OrdersService } from "../../services/orders.service";
import styles from "./ProductOrders.module.scss";

interface InitialProductOrderFilterState {
  order_code: string;
  user_id: string;
  start_date: string;
  end_date: string;
}

const initialFilters: InitialProductOrderFilterState = {
  order_code: "",
  user_id: "",
  start_date: "",
  end_date: "",
};

const ProductOrders = () => {
  const [ordersList, setOrdersList] = useState<ProductOrderItemType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterValues, setFilterValues] =
    useState<InitialProductOrderFilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<InitialProductOrderFilterState>(initialFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedOrder, setSelectedOrder] =
    useState<ProductOrderItemType | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  const handleFetchOrders = async (
    page: number,
    filtersToApply: InitialProductOrderFilterState
  ) => {
    setIsLoading(true);

    const params: ProductOrdersFilterParams = {
      page,
      order_code: filtersToApply.order_code,
      user_id: filtersToApply.user_id,
      start_date: filtersToApply.start_date,
      end_date: filtersToApply.end_date,
    };

    const response = await OrdersService.getProductOrders(params);

    if (!response?.data?.status) {
      setIsLoading(false);
      return;
    }

    const paginationData = response.data.data;
    const items = Array.isArray(paginationData?.data)
      ? paginationData.data
      : [];

    setOrdersList(
      items.map((item: ProductOrderItemType, index: number) => ({
        ...item,
        key: item.id || index + 1,
      }))
    );
    setTotalCount(paginationData?.total || 0);
    setCurrentPage(paginationData?.current_page || page);
    if (paginationData?.per_page) {
      setPageSize(paginationData.per_page);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleFetchOrders(1, initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (
    field: keyof InitialProductOrderFilterState,
    value: string
  ) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleKeyDownFilter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    handleFilterSubmit();
  };

  const handleFilterSubmit = () => {
    setCurrentPage(1);
    setAppliedFilters(filterValues);
    handleFetchOrders(1, filterValues);
  };

  const handleResetFilters = () => {
    setFilterValues(initialFilters);
    setAppliedFilters(initialFilters);
    setCurrentPage(1);
    handleFetchOrders(1, initialFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleFetchOrders(page, appliedFilters);
  };

  const handleOpenDetailModal = (record: ProductOrderItemType) => {
    setSelectedOrder(record);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedOrder(null);
  };

  const formatPhoneNumber = (num: string) => {
    if (!num) {
      return "-";
    }
    const cleaned = num.trim();
    if (cleaned.startsWith("+994")) {
      return cleaned;
    }
    if (cleaned.startsWith("994")) {
      return `+${cleaned}`;
    }
    return `+994 ${cleaned}`;
  };

  const getPhoneHref = (num: string) => {
    if (!num) {
      return "";
    }
    const stripped = num.replace(/\s+/g, "");
    if (stripped.startsWith("+994")) {
      return `tel:${stripped}`;
    }
    if (stripped.startsWith("994")) {
      return `tel:+${stripped}`;
    }
    return `tel:+994${stripped}`;
  };

  const handleDisabledStartDate = (current: dayjs.Dayjs) => {
    if (!current) {
      return false;
    }
    if (current > dayjs().endOf("day")) {
      return true;
    }
    if (
      filterValues.end_date &&
      current > dayjs(filterValues.end_date).endOf("day")
    ) {
      return true;
    }
    return false;
  };

  const handleDisabledEndDate = (current: dayjs.Dayjs) => {
    if (!current) {
      return false;
    }
    if (current > dayjs().endOf("day")) {
      return true;
    }
    if (
      filterValues.start_date &&
      current < dayjs(filterValues.start_date).startOf("day")
    ) {
      return true;
    }
    return false;
  };

  const renderProcurementStatus = (status: string) => {
    if (!status) {
      return (
        <span className={`${styles.status_badge} ${styles.default}`}>-</span>
      );
    }

    const normalized = status.toLowerCase();
    if (normalized === "paid") {
      return (
        <span className={`${styles.status_badge} ${styles.paid}`}>
          Ödənilib
        </span>
      );
    }
    if (normalized === "pending" || normalized === "unpaid") {
      return (
        <span className={`${styles.status_badge} ${styles.pending}`}>
          Gözləmədə
        </span>
      );
    }
    if (normalized === "failed" || normalized === "cancelled") {
      return (
        <span className={`${styles.status_badge} ${styles.cancelled}`}>
          Ləğv edilib
        </span>
      );
    }

    return (
      <span className={`${styles.status_badge} ${styles.default}`}>
        {status}
      </span>
    );
  };

  const renderDeliveryPaidStatus = (
    deliveryStatus: boolean | string | number | null
  ) => {
    if (
      deliveryStatus === null ||
      deliveryStatus === undefined ||
      deliveryStatus === ""
    ) {
      return (
        <span className={`${styles.delivery_paid_badge} ${styles.not_paid}`}>
          Məlum deyil
        </span>
      );
    }

    if (
      deliveryStatus === true ||
      deliveryStatus === "paid" ||
      deliveryStatus === 1 ||
      deliveryStatus === "1"
    ) {
      return (
        <span className={`${styles.delivery_paid_badge} ${styles.paid}`}>
          Ödənilib
        </span>
      );
    }

    return (
      <span className={`${styles.delivery_paid_badge} ${styles.not_paid}`}>
        Ödənilməyib
      </span>
    );
  };

  const tableColumns: TableProps<ProductOrderItemType>["columns"] = [
    {
      title: "№",
      key: "row_number",
      width: 60,
      align: "center",
      render: (_, __, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "ID",
      dataIndex: "id",
      width: 75,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Sifariş kodu",
      dataIndex: "order_code",
      render: (code: string) => (
        <span className={styles.order_code_badge}>{code || "-"}</span>
      ),
    },
    {
      title: "Müştəri",
      key: "customer_name",
      render: (_, record) => {
        const fullName = `${record.name || ""} ${record.surname || ""}`.trim();
        return fullName || "-";
      },
    },
    {
      title: "E-poçt",
      dataIndex: "email",
      render: (email: string) =>
        email ? (
          <a
            href={`mailto:${email}`}
            className={styles.link_cell}
            aria-label={`E-poçt göndər: ${email}`}
            tabIndex={0}
          >
            {email}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Nömrə",
      dataIndex: "number",
      render: (number: string) =>
        number ? (
          <a
            href={getPhoneHref(number)}
            className={styles.link_cell}
            aria-label={`Zəng et: ${formatPhoneNumber(number)}`}
            tabIndex={0}
          >
            {formatPhoneNumber(number)}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "İstifadəçi ID",
      dataIndex: "user_id",
      width: 105,
      align: "center",
      render: (userId: number) => userId || "-",
    },
    {
      title: "Məhsul sayı",
      dataIndex: "total_quantity",
      width: 105,
      align: "center",
      render: (qty: number) => (
        <span className={styles.quantity_badge}>{qty ?? 0}</span>
      ),
    },
    {
      title: "Məbləğ",
      dataIndex: "total_price",
      width: 115,
      render: (price: string) => (
        <span className={styles.price_text}>
          {price ? `${price} AZN` : "-"}
        </span>
      ),
    },
    {
      title: "Tədarük statusu",
      dataIndex: "procurement_status",
      width: 135,
      render: (status: string) => renderProcurementStatus(status),
    },
    {
      title: "Çatdırılma ödənişi",
      dataIndex: "delivery_is_paid",
      width: 145,
      render: (status: any) => renderDeliveryPaidStatus(status),
    },
    {
      title: "Sifariş tarixi",
      dataIndex: "created_at",
      width: 155,
      render: (date: string) => date || "-",
    },
    {
      title: "Əməliyyatlar",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div className={styles.action_buttons}>
          <button
            type="button"
            onClick={() => handleOpenDetailModal(record)}
            className={`${styles.action_btn} ${styles.view_btn}`}
            aria-label={`Ətraflı bax: ${record.order_code}`}
            tabIndex={0}
          >
            <FaEye size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Məhsul sifarişləri</title>
      </Helmet>

      <div className={styles.orders_container}>
        <div className={styles.page_header}>
          <h1 className={styles.page_title}>Məhsul sifarişləri</h1>
          <span className={styles.total_badge}>
            Toplam sifariş: <strong>{totalCount}</strong>
          </span>
        </div>

        {/* Filters Section: Exactly start_date, end_date, order_code, user_id */}
        <section aria-label="Sifariş filtrləri" className={styles.filter_card}>
          <div className={styles.filter_grid}>
            <div className={styles.filter_item}>
              <label htmlFor="filter-order-code">Sifariş kodu</label>
              <input
                id="filter-order-code"
                type="text"
                value={filterValues.order_code}
                onChange={(e) =>
                  handleInputChange("order_code", e.target.value)
                }
                onKeyDown={handleKeyDownFilter}
                placeholder="ORD..."
                className={styles.filter_input}
                tabIndex={0}
                aria-label="Sifariş kodu üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-user-id">İstifadəçi ID</label>
              <input
                id="filter-user-id"
                type="text"
                value={filterValues.user_id}
                onChange={(e) => handleInputChange("user_id", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                placeholder="ID daxil edin"
                className={styles.filter_input}
                tabIndex={0}
                aria-label="İstifadəçi ID üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label>Başlanğıc tarixi</label>
              <DatePicker
                className={styles.filter_date}
                placeholder="Başlanğıc tarixi"
                format="YYYY-MM-DD"
                disabledDate={handleDisabledStartDate}
                value={
                  filterValues.start_date
                    ? dayjs(filterValues.start_date)
                    : null
                }
                onChange={(_, dateString) =>
                  handleInputChange(
                    "start_date",
                    typeof dateString === "string" ? dateString : ""
                  )
                }
              />
            </div>

            <div className={styles.filter_item}>
              <label>Bitmə tarixi</label>
              <DatePicker
                className={styles.filter_date}
                placeholder="Bitmə tarixi"
                format="YYYY-MM-DD"
                disabledDate={handleDisabledEndDate}
                value={
                  filterValues.end_date ? dayjs(filterValues.end_date) : null
                }
                onChange={(_, dateString) =>
                  handleInputChange(
                    "end_date",
                    typeof dateString === "string" ? dateString : ""
                  )
                }
              />
            </div>
          </div>

          <div className={styles.filter_actions}>
            <button
              type="button"
              onClick={handleResetFilters}
              className={styles.reset_btn}
              tabIndex={0}
              aria-label="Filtrləri sıfırla"
            >
              <FaUndo size={13} />
              Sıfırla
            </button>
            <MainButton onClick={handleFilterSubmit}>
              <div className={styles.search_btn_content}>
                <FaSearch size={13} />
                <span>Axtar</span>
              </div>
            </MainButton>
          </div>
        </section>

        {/* Table Section */}
        <section
          aria-label="Məhsul sifarişləri cədvəli"
          className={styles.table_card}
        >
          {isLoading ? (
            <div
              style={{
                padding: "64px 0",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Spinner />
            </div>
          ) : (
            <div className={styles.table_wrapper}>
              <Table<ProductOrderItemType>
                columns={tableColumns}
                dataSource={ordersList}
                rowKey={(record) => String(record.id)}
                scroll={{ x: 1200 }}
                locale={{
                  emptyText: "Məlumat tapılmadı",
                  triggerDesc: "Azalan sıra ilə düz",
                  triggerAsc: "Artan sıra ilə düz",
                  cancelSort: "Sıralamanı ləğv et",
                }}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: false,
                  onChange: handlePageChange,
                  showTotal: (total, range) => (
                    <span className={styles.total_text}>
                      Göstərilir: {range[0]}-{range[1]} / Cəmi: {total}
                    </span>
                  ),
                }}
              />
            </div>
          )}
        </section>
      </div>

      {/* Order Detail Modal */}
      <Modal
        title={
          <div className={styles.modal_title}>
            Sifariş Məlumatları #{selectedOrder?.id}
          </div>
        }
        open={isDetailModalOpen}
        onCancel={handleCloseDetailModal}
        width={620}
        footer={[
          <button
            key="close"
            type="button"
            onClick={handleCloseDetailModal}
            className={styles.modal_close_btn}
            tabIndex={0}
            aria-label="Bağla"
          >
            Bağla
          </button>,
        ]}
      >
        {selectedOrder && (
          <div>
            <div className={styles.modal_order_header}>
              <div className={styles.order_avatar}>
                <FaBoxOpen />
              </div>
              <div className={styles.order_headline}>
                <h3>{selectedOrder.order_code}</h3>
                <span className={styles.order_sub_id}>
                  Müştəri: {selectedOrder.name} {selectedOrder.surname}
                </span>
              </div>
            </div>

            <div className={styles.info_list}>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Sifariş ID:</span>
                <span className={styles.info_value}>{selectedOrder.id}</span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Sifariş Kodu:</span>
                <span className={styles.info_value}>
                  <span className={styles.order_code_badge}>
                    {selectedOrder.order_code}
                  </span>
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Müştəri Adı:</span>
                <span className={styles.info_value}>
                  {selectedOrder.name} {selectedOrder.surname}
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>İstifadəçi ID:</span>
                <span className={styles.info_value}>
                  {selectedOrder.user_id || "-"}
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>E-poçt:</span>
                <span className={styles.info_value}>
                  {selectedOrder.email ? (
                    <a
                      href={`mailto:${selectedOrder.email}`}
                      className={styles.link_cell}
                    >
                      {selectedOrder.email}
                    </a>
                  ) : (
                    "-"
                  )}
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Əlaqə nömrəsi:</span>
                <span className={styles.info_value}>
                  {selectedOrder.number ? (
                    <a
                      href={getPhoneHref(selectedOrder.number)}
                      className={styles.link_cell}
                    >
                      {formatPhoneNumber(selectedOrder.number)}
                    </a>
                  ) : (
                    "-"
                  )}
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Ümumi Məbləğ:</span>
                <span className={styles.info_value}>
                  <strong>{selectedOrder.total_price} AZN</strong>
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Ümumi Sayı:</span>
                <span className={styles.info_value}>
                  {selectedOrder.total_quantity} ədəd
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Tədarük Statusu:</span>
                <span className={styles.info_value}>
                  {renderProcurementStatus(selectedOrder.procurement_status)}
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Çatdırılma Ödənişi:</span>
                <span className={styles.info_value}>
                  {renderDeliveryPaidStatus(selectedOrder.delivery_is_paid)}
                </span>
              </div>

              <div className={styles.info_row}>
                <span className={styles.info_label}>Sifariş Tarixi:</span>
                <span className={styles.info_value}>
                  {selectedOrder.created_at || "-"}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductOrders;
